import { z } from 'zod'
import { scribeSourceLanguageSchema } from '../scribe/handlers/encounter-language.contract'
import { billingSuggestionSchema, rosEntrySchema } from '../scribe/draft-response.contract'

/**
 * Zod source of truth for the Progress Note SOAP payload (replaces the former
 * class-validator `soap-note.dto.ts`). One schema drives THREE things: the oRPC
 * `/v2/visits/soap-note` boundary validation, the lock-time replay validation
 * (`lock-progress-note.handler.ts` — was `validateSync`), and the internal type
 * consumed by the CDA pipeline.
 *
 * Fidelity note: mirrors the former DTO field-for-field. Objects use zod's
 * default key-strip (NOT `.strict()`/`.passthrough()`) to preserve the old
 * `ValidationPipe({ whitelist: true })` behavior — `visit.utils.ts` iterates the
 * remaining keys to build the QuestionnaireResponse, so extra keys must be
 * dropped, not passed through.
 */

export const codingSchema = z.object({
  system: z.string().optional(),
  code: z.string().optional(),
  display: z.string().optional(),
})
export type Coding = z.infer<typeof codingSchema>

// Medication coding — RxNorm identity + optional dosing that round-trips to
// MedicationStatement.dosage[] / .status on the cascade.
export const treatmentSchema = codingSchema.extend({
  dose: z.string().optional(),
  route: z.string().optional(),
  frequency: z.string().optional(),
  note: z.string().optional(),
  medicationStatus: z.enum(['active', 'completed', 'intended', 'stopped', 'on-hold']).optional(),
})
export type Treatment = z.infer<typeof treatmentSchema>

// Diagnosis / problem coding — ICD-10 identity + the two chart-rendered fields
// (clinicalStatus → Status, onsetDateTime → Started).
export const conditionSchema = codingSchema.extend({
  clinicalStatus: z
    .enum(['active', 'recurrence', 'relapse', 'inactive', 'remission', 'resolved'])
    .optional(),
  onsetDateTime: z.string().optional(),
  // Chart-seeded row (mirrors an existing chart Condition); skipped at lock, chart never written.
  external: z.boolean().optional(),
})
export type Condition = z.infer<typeof conditionSchema>

export const allergySchema = codingSchema.extend({
  clinicalStatus: z.enum(['active', 'inactive', 'resolved']).optional(),
  reaction: z.string().optional(),
  severity: z.enum(['mild', 'moderate', 'severe']).optional(),
  category: z.enum(['food', 'medication', 'environment', 'biologic']).optional(),
  external: z.boolean().optional(),
  // PD-1141 — source chart AllergyIntolerance id for external rows; survives
  // lock in the draft blob, never written back to the chart.
  chartId: z.string().optional(),
  // PD-1141 — reaction/status changed during this encounter (display marker only).
  updated: z.boolean().optional(),
})
export type Allergy = z.infer<typeof allergySchema>

export const serviceRequestSchema = codingSchema.extend({
  category: z.enum(['laboratory', 'imaging', 'referral']).optional(),
  intent: z.enum(['order', 'plan', 'proposal']).optional(),
  priority: z.enum(['routine', 'urgent', 'asap', 'stat']).optional(),
  reasonText: z.string().optional(),
})
export type ServiceRequest = z.infer<typeof serviceRequestSchema>

export const billingItemSchema = z.object({
  // Optional to match the former DTO (`dx?` in the generated OpenAPI) — the
  // billing modal's "+ Add diagnosis" pushes a dx-less row that must still save.
  dx: codingSchema.optional(),
})
export type BillingItem = z.infer<typeof billingItemSchema>

export const billingEntrySchema = z.object({
  // `@IsOptional` in the former DTO — a procedure-less row passed the boundary,
  // so keep it optional to avoid newly rejecting mid-edit autosaves.
  procedure: codingSchema.optional(),
  price: z.number().optional(),
  quantity: z.number(),
  billingItems: z.array(billingItemSchema),
  modifiers: z.array(z.string()).default([]),
})
export type BillingEntry = z.infer<typeof billingEntrySchema>

export { billingSuggestionSchema, rosEntrySchema }
export type { BillingSuggestion, RosEntry } from '../scribe/draft-response.contract'

/** Chart-derived Family History rows, grouped by relative (PD-1377). Blob-only, like structured ROS. */
export const familyHistoryEntrySchema = z.object({
  relative: z.string(),
  conditions: z.array(z.string()),
  deceased: z.boolean().optional(),
})
export type FamilyHistoryEntry = z.infer<typeof familyHistoryEntrySchema>

export const soapNoteSchema = z.object({
  // Essential
  noteDate: z.string().optional(),
  visitType: z.enum(['new', 'follow-up']),
  examReason: z.string(),
  prob: z.string(),
  objective: z.string(),
  assessment: z.string(),
  plan: z.string(),

  // Vitals
  bpSystolic: z.string(),
  bpDiastolic: z.string(),
  bpNote: z.string(),
  hr: z.string(),
  hrNote: z.string(),
  wt: z.string(),
  wtNote: z.string(),
  wtUnit: z.enum(['[lb_av]', 'kg']).optional(),
  bloodGlucose: z.string().optional(),
  glucoseUnit: z.enum(['mg/dL', 'mmol/L']).optional(),
  bloodGlucoseNote: z.string().optional(),
  pain: z.string(),
  painNote: z.string(),
  ht: z.string(),
  htNote: z.string(),
  htUnit: z.enum(['[in_i]', 'cm']).optional(),
  hc: z.string(),
  hcNote: z.string(),
  rr: z.string(),
  rrNote: z.string(),
  o2: z.string(),
  o2Note: z.string(),
  temp: z.string(),
  tempNote: z.string(),
  tempUnit: z.enum(['[degF]', 'Cel']).optional(),
  bmi: z.string(),

  // Coding arrays
  probHPI: z.array(codingSchema),
  allergies: z.array(allergySchema),
  pastMedicalHistory: z.array(conditionSchema),
  pastSurgicalHistory: z.array(codingSchema),
  familyHistory: z.array(codingSchema),
  // PD-1377 structured slot. Optional: notes written before it have none, and
  // this schema is re-validated at lock — never repurpose `familyHistory` above.
  familyHistoryStructured: z.array(familyHistoryEntrySchema).optional(),
  socialHistory: z.array(codingSchema),
  habits: z.array(codingSchema),
  medsReviewed: z.array(treatmentSchema),
  reviewOfSystems: z.array(codingSchema),
  reviewOfSystemsStructured: z.array(rosEntrySchema).optional(),
  physicalExam: z.array(codingSchema),
  dataReviewed: z.array(codingSchema),
  assessment1: z.array(conditionSchema),
  testsOrdered: z.array(serviceRequestSchema),
  treatmentsPrescribed: z.array(treatmentSchema),
  proceduresAdministered: z.array(codingSchema),
  carePlanGoals: z.array(codingSchema),
  followUp: z.string(),
  // PD-1321 Medical Decision Making narrative. Optional: notes written before
  // PD-1321 have none, and this schema is re-validated at lock.
  mdm: z.string().optional(),
  tags: z.array(z.string()),
  implausibleVitals: z.array(z.string()).optional(),
  // PD-1613 — true once a whole-note AI action reached this note; `saveDraft`
  // keeps it true on every later save. Optional: pre-release blobs lack it and
  // this schema is re-validated at lock.
  aiAssisted: z.boolean().optional(),
  // PD-1600 — the language the encounter was conducted in, set only when it was
  // not English (the note itself is always English); sticky like `aiAssisted`.
  encounterLanguage: scribeSourceLanguageSchema.optional(),
  // PD-1600 — rows the AI marked low-confidence that the doctor has not yet
  // confirmed or edited (`lowConfidenceKey`); blocks Lock like `implausibleVitals`.
  lowConfidenceFields: z.array(z.string()).optional(),

  // Billing
  selectedCoverageId: z.string().optional(),
  billing: z.array(billingEntrySchema),
  billingSuggestion: billingSuggestionSchema.optional(),
})
export type SoapNote = z.infer<typeof soapNoteSchema>

// PD-1958 — a draft save may leave `billing` out when the note's copy was incomplete
// as loaded (a diagnosis it could not read); the Claim and the stored blob keep theirs.
export const soapNoteDraftSchema = soapNoteSchema.extend({
  billing: soapNoteSchema.shape.billing.optional(),
})
export type SoapNoteDraft = z.infer<typeof soapNoteDraftSchema>

export const visitContextSchema = z.object({
  patientId: z.string(),
  practitionerId: z.string(),
  practitionerDisplay: z.string().optional(),
  projectId: z.string(),
  organizationId: z.string(),
  scribeSessionId: z.string().optional(),
})
export type VisitContext = z.infer<typeof visitContextSchema>
